import * as angular from 'angular';
import { IControllerService, ILocationService, IScope } from 'angular';
import { config, embedded } from 'angular/utils/config';
import { migratedBiddingProcess } from 'angular/utils/migrated-auction-bidding-process';
import { USER_ROLES } from 'app/_constants/user-roles.constant';
import { AuthService } from 'app/_services/auth.service';
import { PrivilegeService } from 'app/_services/privilegeService';
import { getCognitoTokens } from 'angular/utils/cognito';
import { NetworkPointResource } from 'app/_resources/network-point.resource';
import { migratedComfortBids } from 'angular/utils/migrated-comfort-bids';

const targetLocation = embedded ? window.parent.location : window.location;

const { appBaseUrl } = config;

/**
 * TODO migrate to angular 7+ router when every component is migrated to angular 7+
 */
function routes(
    $routeProvider: angular.route.IRouteProvider,
    USER_ROLES: USER_ROLES
) {
    let requireAuthentication = [
        'privilegeService',
        function (privilegeService: PrivilegeService) {
            return privilegeService.hasPrivilege();
        }
    ];

    let loginResult = [
        'authService',
        function (authService: AuthService) {
            return authService.userLoginPromise.promise.then(
                (res) => res,
                () => undefined
            );
        }
    ];

    let acerTradeAllowed = [
        'ExternalTrade',
        function (ExternalTrade) {
            return ExternalTrade.shipperCreateExternalTradeAllowed().$promise.then(
                (res) => !!res,
                () => false
            );
        }
    ];

    const secondaryProposalDetails = {
        template: `<pkp-secondary-proposal-details id="$resolve.id" 
                                                  authenticated-user="$resolve.authenticatedUser">
                           </pkp-secondary-proposal-details>`,
        resolve: {
            id: ($route: angular.route.IRouteService) =>
                $route.current.params.id,
            authenticatedUser: loginResult
        }
    };
    secondaryProposalDetails.resolve.id.$inject = ['$route'];

    // customize $routeProvider.when to always ask for an up-to-date user.
    // the big problem with the existing angular and monolith code is that
    // many endpoints don't return 401s when being called by an unauthenticated user,
    // but 204s or something else. to ensure a consistent behaviour, we always check the user.
    const originalWhen = $routeProvider.when;
    $routeProvider.when = function customWhen(path, route) {
        route.resolve || (route.resolve = {});
        angular.extend(route.resolve, {
            __refreshAuth: [
                '$rootScope',
                ($rootScope: any) => {
                    if (!$rootScope.checkAuth) return; // initially not set, which is fine as it is called automatically in the controller that sets it
                    return $rootScope.checkAuth(); // configured in angular/src/main/webapp/app/security/formular.ts
                }
            ]
        });
        return originalWhen.call($routeProvider, path, route);
    };

    $routeProvider
        .when('/start', {
            template: `
<ng-include src="'app/dashboard/dashboard.html'" ng-if="$resolve.role === 'SHIPPER_USER' || $resolve.role === 'SHIPPER_ADMIN'"></ng-include> 
<ng-include src="'app/tso-dashboard/tso-dashboard.html'" ng-if="$resolve.role === 'TSO_USER'"></ng-include>`,
            resolve: {
                authenticatedUser: loginResult,
                role: (authService: AuthService) =>
                    authService.userLoginPromise.promise
                        .then((res) => res.role)
                        .catch(() => '')
            },
            controller: async (
                authService: AuthService,
                $scope: IScope,
                $controller: IControllerService,
                $location: ILocationService
            ) => {
                const controllerByRole = {
                    SHIPPER_ADMIN: 'DashboardCtrl as dashboard',
                    SHIPPER_USER: 'DashboardCtrl as dashboard',
                    SSO_ADMIN: 'TsoDashboardCtrl as dashboard',
                    TSO_USER: 'TsoDashboardCtrl as dashboard'
                };

                const tokens = await getCognitoTokens();

                try {
                    const loggedInUser =
                        await authService.userLoginPromise.promise;
                    // LSO admin is also treated as SSO_ADMIN
                    if (loggedInUser.role === 'SSO_ADMIN') {
                        targetLocation.replace(`${appBaseUrl}/dashboard`);
                    } else {
                        $controller(controllerByRole[loggedInUser.role], {
                            $scope: $scope
                        });
                    }
                } catch (error) {
                    // some users are not known by the monolith, but might be logged in
                    // via cognito
                    if (tokens)
                        targetLocation.replace(`${appBaseUrl}/dashboard`);
                    else targetLocation.replace(`${appBaseUrl}/`); // guest user
                }
            }
        })
        .when('/loading', {
            template: '<div>Loading...</div>'
        })
        .when('/initialize', {
            template: '<div>Loading...</div>'
        })
        .when('/notifications', {
            template:
                '<pkp-tso-notification-overview></pkp-tso-notification-overview>',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/activity', {
            template: require('app/activity/activities.html'),
            controller: 'ActivitiesCtrl',
            controllerAs: 'ActivitiesCtrl',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/create/trade', {
            template: '<pkp-trade-upload-wizard></pkp-trade-upload-wizard>'
        })
        .when('/uploads/create/product', {
            template: `<pkp-product-upload-wizard wizard-name="Manual product upload">
                           </pkp-product-upload-wizard>`,
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/edit/product/:rawDataId?', {
            template: `<pkp-product-upload-wizard wizard-name="Edit upload"
                                            raw-data-id="$resolve.rawDataId">
                           </pkp-product-upload-wizard>`,
            resolve: {
                rawDataId: ($route: angular.route.IRouteService) =>
                    $route.current.params.rawDataId,
                access: requireAuthentication
            }
        })
        .when('/uploads/create/portfolio-code', {
            template: `<pkp-portfolio-code-upload-wizard></pkp-portfolio-code-upload-wizard>`,
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/create/balancing-group', {
            template: `<pkp-balancing-group-upload-wizard></pkp-balancing-group-upload-wizard>`,
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/create/reverse-auction', {
            template: `<pkp-reverse-auction-upload-wizard></pkp-reverse-auction-upload-wizard>`,
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads/xml', {
            template: require('app/upload-xml/xmlupload.html'),
            controller: 'XmlUploadsCtrl',
            controllerAs: 'xmlUpload',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/uploads', {
            template: require('app/uploads/uploads.html'),
            controller: 'UploadsCtrl',
            controllerAs: 'uploads',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/shippers', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/shippers/list`);
            }
        })
        .when('/settings/company-information', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/company-information`
                );
            }
        })
        .when('/settings', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/general`
                );
            }
        })
        .when('/settings/general', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/general`
                );
            }
        })
        .when('/settings/auctions', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/auctions`
                );
            }
        })
        .when('/settings/secondary-market', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/secondary-trading`
                );
            }
        })
        .when('/settings/surrender', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/surrender`
                );
            }
        })
        .when('/settings/fcfs', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/regular-fcfs`
                );
            }
        })
        .when('/settings/subscriptions', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/subscriptions`
                );
            }
        })
        .when('/settings/credit-limits', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/operator-settings/credit-limits`
                );
            }
        })
        .when('/operator-settings/user-management', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/organisation/users`
                );
            }
        })
        .when('/operator-settings/:activeSetting?', {
            template:
                '<pkp-operator-settings active-setting="$resolve.activeSetting"></pkp-operator-settings>',
            resolve: {
                activeSetting: ($route: angular.route.IRouteService) =>
                    $route.current.params.activeSetting,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(
                        USER_ROLES.TSO_USER,
                        USER_ROLES.SSO_ADMIN
                    )
            }
        })
        .when('/shippers/details/:customerRefId/shipper-info', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/shippers/details/${$routeParams.customerRefId}`
                );
            }
        })
        .when('/shippers/details/:customerRefId', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/shippers/details/${$routeParams.customerRefId}`
                );
            }
        })
        .when('/shippers/details/:customerRefId/documents', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/shippers/${$routeParams.customerRefId}/documents`
                );
            }
        })
        .when('/shippers/details/:customerRefId/:subPage?', {
            template:
                '<pkp-shippers-details sub-page="$resolve.subPage" customer-ref-id="$resolve.customerRefId"></pkp-shippers-details>',
            resolve: {
                subPage: ($route: angular.route.IRouteService) =>
                    $route.current.params.subPage,
                customerRefId: ($route: angular.route.IRouteService) =>
                    $route.current.params.customerRefId,
                access: requireAuthentication
            }
        })
        .when('/market-information/players/tso/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/players/tsos/${$routeParams.id}`
                );
            }
        })
        .when('/market-information/players/shipper', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/players/shipper`
                );
            }
        })
        .when('/market-information/players/tsos', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/players/tsos`
                );
            }
        })
        .when('/market-information/players', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/players/tsos`
                );
            }
        })
        .when('/market-information/rates', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/rates/current`
                );
            }
        })
        .when('/market-information/rates/current', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/rates/current`
                );
            }
        })
        .when('/market-information/rates/archive', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/market-information/rates/archive`
                );
            }
        })
        .when('/market-information/:page?/:subPage?/:id?', {
            template: require('app/market-info/marketInfo.html'),
            controller: 'MarketInfoCtrl',
            controllerAs: 'marketInfo'
        })
        .when('/account-settings/premium-services', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/account-settings/premium-services`
                );
            }
        })
        .when('/account-settings/contacts', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/organisation/contacts`
                );
            }
        })
        .when('/account-settings/company-information', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/shipper-settings/company-information`
                );
            }
        })
        .when(
            '/account-settings/assignment-of-tsos/contracting-services/:id?',
            {
                template: require('app/account-settings/contracting-services.html'),
                controller() {},
                resolve: {
                    access: requireAuthentication
                }
            }
        )
        .when('/platform-settings/general', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/general`
                );
            }
        })
        .when('/platform-settings/dashboard', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/dashboard`
                );
            }
        })
        .when('/platform-settings/network-points', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/network-points`
                );
            }
        })
        .when('/platform-settings/trader-lists', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                );
            }
        })
        .when('/platform-settings/secondary-trading', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                );
            }
        })
        .when('/platform-settings/new-trader-list', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/new-trader-list`
                );
            }
        })
        .when('/platform-settings/conditions', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/user-settings/secondary-trading`
                );
            }
        })
        .when('/user-settings/:page?/:subPage?/:id?', {
            template: require('app/platform-settings/platform-settings.html'),
            controller: 'PlatformSettingsCtrl',
            controllerAs: 'platSettings',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/shipper-settings/company-information', {
            template: '',
            controller() {
                targetLocation.replace(
                    `/settings/organisation/company-information`
                );
            }
        })
        .when('/shipper-admin', {
            template: '',
            controller() {
                targetLocation.replace(
                    `/settings/organisation/company-information`
                );
            }
        })
        .when('/shipper-admin/register', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/organisation/users`
                );
            }
        })
        .when('/organisation/users', {
            template: '',
            resolve: {
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(
                        USER_ROLES.TSO_USER,
                        USER_ROLES.SSO_ADMIN,
                        USER_ROLES.SHIPPER_ADMIN
                    )
            }
        })
        .when('/shipper-admin/register/confirmation', {
            template:
                '<pkp-shipper-admin-user-registration-confirmation-page>' +
                '</pkp-shipper-admin-user-registration-confirmation-page>'
        })
        .when('/shipper-admin/:userId', {
            template: '<pkp-shipper-admin-user></pkp-shipper-admin-user>',
            resolve: {
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SHIPPER_ADMIN)
            }
        })
        .when('/shipper-admin/details/:userId', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/organisation/users`
                );
            }
        })
        .when('/shipper-admin/details/:userId/edit-assignments', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/operators/assignments/overview`
                );
            }
        })
        .when('/fcfs-booking/:stage?', {
            template: require('app/fcfs-booking/fcfs-booking.html'),
            controller: 'FcfsBookingCtrl',
            controllerAs: 'fcfsBooking',
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/auctions', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/transport/auctions`);
            }
        })
        .when('/auctions/list', {
            controller() {
                if (!migratedBiddingProcess.isActive) return;
                const search = location.hash.replace('#/auctions/list', '');
                targetLocation.replace(
                    `${appBaseUrl}/transport/auctions/list${search}`
                );
            },
            template: `<pkp-auction-list logged-in-user="$resolve.authenticatedUser"></pkp-auction-list>`,
            reloadOnSearch: false,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/multi-year-auctions', {
            template: `<pkp-multi-year-capacity></pkp-multi-year-capacity>`
        })
        .when('/multi-year-auctions/:id', {
            template: `<pkp-multi-year-capacity-details [auction-id]="$resolve.id"></pkp-multi-year-capacity-details>`,
            resolve: {
                id: ($route) => $route.current.params.id
            }
        })
        .when('/auctions/reverse/:id?', {
            template: require('app/reverse-auction-bid/reverse-auction-bid.html'),
            controller: 'ReverseAuctionBidCtrl',
            controllerAs: 'reverseAuctionBid'
        })
        .when('/auctions/:auctionId/:bidOrDetails', {
            template: require('app/auction-bid/bid.html'),
            controller: 'AuctionBidCtrl',
            controllerAs: 'auctionBid',
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/auctions/:auctionId/bid/:bidId/edit/', {
            template: require('app/auction-bid/edit.html'),
            controller: 'AuctionBidEditCtrl',
            controllerAs: 'auctionBidEdit',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/secondary-overview/trades/details/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/transport/secondary/trades/${$routeParams.id}`
                );
            }
        })
        .when('/secondary-overview/proposals/details/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/transport/secondary/proposals/${$routeParams.id}`
                );
            }
        })
        .when('/secondary-trading/details/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/transport/secondary/proposals/${$routeParams.id}`
                );
            }
        })
        .when('/secondary-overview/:type', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/transport/secondary/${$routeParams.type}`
                );
            }
        })
        .when('/secondary-overview', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/transport/secondary`);
            }
        })
        .when('/secondary-trading', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/transport/secondary`);
            }
        })
        .when('/create-proposal/:stage?', {
            template: require('app/create-proposal/create-proposal.html'),
            controller: 'CreateProposalCtrl',
            controllerAs: 'createProposal',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/create-external-trade/:stage?', {
            redirectTo: '/reporting/secondary/external-trades'
        })
        .when('/edit-proposal/:id?/:stage?', {
            template: require('app/create-proposal/create-proposal.html'),
            controller: 'CreateProposalCtrl',
            controllerAs: 'createProposal',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/comfort-bid/create', {
            template: `<pkp-wizard-host wizard-name="Day-ahead comfort bid" 
                                            template-url="app/comfort-bid/comfort-bid-wizard.component.html">
                           </pkp-wizard-host>`,
            controller() {
                if (migratedComfortBids.isNewDesignActive) {
                    migratedComfortBids.goToNewDesign();
                    return;
                }
            },
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/comfort-bid/edit/:id?', {
            template: `<pkp-comfort-bid-edit comfort-bid-id="$resolve.id"></pkp-comfort-bid-edit>`,
            controller($routeParams) {
                if (migratedComfortBids.isNewDesignActive) {
                    migratedComfortBids.goToNewDesign({
                        comfortBidId: $routeParams.id
                    });
                    return;
                }
            },
            resolve: {
                id: ($route: ng.route.IRouteService) => $route.current.params.id
            }
        })
        .when('/surrender-capacity/:networkPointId', {
            template: require('app/capacity-surrender/surrender-capacity.html'),
            controller: 'CapacitySurrenderCtrl',
            controllerAs: 'surrenderCapacity',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/network-point', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/transport/network-points`
                );
            }
        })
        .when('/network-point/details/:networkPointId', {
            template:
                '<pkp-network-point-details-page network-point-id="$resolve.id"></pkp-network-point-details-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.networkPointId,
                authenticatedUser: loginResult
            }
        })
        .when('/network-point/create/:networkPointId?', {
            template:
                '<pkp-network-point-details-page network-point-id="$resolve.id"></pkp-network-point-details-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.networkPointId,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/transport/bundle-requests`
                );
            }
        })
        .when('/network-point/bundle/details/:id', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/create', {
            template:
                '<pkp-network-point-bundle-create-page network-point-for-creation="$resolve.networkPointForCreation"></pkp-network-point-bundle-create-page>',
            resolve: {
                networkPointForCreation: (
                    $route: angular.route.IRouteService,
                    NetworkPoint: NetworkPointResource
                ) =>
                    NetworkPoint.get({ id: $route.current.params.nwp })
                        .$promise,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/change/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/delete/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/network-point/bundle/conversation/:id?', {
            template:
                '<pkp-network-point-bundle-create-page id="$resolve.id"></pkp-network-point-bundle-create-page>',
            resolve: {
                id: ($route: angular.route.IRouteService) =>
                    $route.current.params.id,
                access: requireAuthentication
            }
        })
        .when('/reporting/new', {
            template: require('app/report-create/report-create.html'),
            controller: 'ReportCreateCtrl',
            controllerAs: 'createCtrl',
            resolve: {
                access: requireAuthentication
            }
        })
        .when('/reporting/secondary/external-trade/details/:tradeId', {
            template:
                '<pkp-external-trade-details trade-id="$resolve.tradeId" is-report="true"></pkp-external-trade-details>',
            resolve: {
                tradeId: ($route: angular.route.IRouteService) =>
                    $route.current.params.tradeId
            }
        })
        .when('/reporting/secondary/trades/details/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/secondary/trades/${$routeParams.id}`
                );
            }
        })
        .when('/reporting/secondary/proposals/details/:id', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/secondary/proposals/${$routeParams.id}`
                );
            }
        })
        .when('/reporting/secondary/:type', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/secondary/${$routeParams.type}`
                );
            }
        })
        .when('/reporting/secondary', {
            redirectTo: '/reporting/secondary/trades'
        })
        .when('/fcfs-booking-maintenance', {
            template: '<pkp-fcfs-maintenance-page></pkp-fcfs-maintenance-page>'
        })
        .when('/reporting/fcfs', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/reporting/fcfs`);
            }
        })
        .when('/reporting/upload/details/:auctionId', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/products/uploaded/${$routeParams.auctionId}`
                );
            }
        })
        .when('/reporting/auction/details/:auctionId', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/auctions/details/${$routeParams.auctionId}`
                );
            }
        })
        .when('/reporting/:reportType/details/:auctionId', {
            template: require('app/reporting-details/parent-report-details.html'),
            controller: 'ReportDetailsCtrl',
            controllerAs: 'reportDetails'
        })
        .when('/reporting/upload', {
            template: '',
            controller() {
                const search = location.hash.replace('#/reporting/upload', '');
                targetLocation.replace(
                    `${appBaseUrl}/reporting/products/uploaded${search}`
                );
            }
        })
        .when('/reporting/auction', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/auctions/short-and-long-term-auctions`
                );
            }
        })
        .when('/reporting/standard', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/reporting/standard`);
            }
        })
        .when('/reporting/pending', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/auctions/pending-auctions`
                );
            }
        })
        .when('/reporting/reverse', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/reporting/auctions/reverse-auctions`
                );
            }
        })
        .when('/reporting/capacity-linking', {
            template: `<pkp-capacity-linking-report-overview></pkp-capacity-linking-report-overview>`
        })
        .when('/reporting/surrender', {
            template: '',
            controller($routeParams) {
                targetLocation.replace(`${appBaseUrl}/reporting/surrenders`);
            }
        })
        .when('/reporting/:reportType?', {
            template: require('app/reporting/parent-report.html'),
            controller: 'ReportingCtrl',
            controllerAs: 'reporting'
        })
        .when('/storage/offers', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/storage/offers`);
            }
        })
        .when('/storage/offers/create', {
            template:
                '<pkp-create-storage-offer authenticated-user="$resolve.authenticatedUser"></pkp-create-storage-offer>',
            resolve: {
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/storage/settings/company-information', {
            template: '',
            controller() {
                targetLocation.replace(
                    `/settings/organisation/company-information`
                );
            }
        })
        .when('/storage/settings', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/storage-settings/premium-services`
                );
            }
        })
        .when('/storage/settings/premium-services', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/storage-settings/premium-services`
                );
            }
        })
        .when('/storage/settings/advanced', {
            template: '',
            controller() {
                targetLocation.replace(
                    `${appBaseUrl}/settings/platform/#/storage-settings/general`
                );
            }
        })
        .when('/storage-settings/:activeSetting?', {
            template:
                '<pkp-storage-settings active-setting="$resolve.activeSetting" authenticated-user="$resolve.authenticatedUser">' +
                '</pkp-storage-settings>',
            resolve: {
                activeSetting: ($route: angular.route.IRouteService) =>
                    $route.current.params.activeSetting,
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/storage/offers/details/:offerId/:action?', {
            template:
                '<pkp-storage-offer-details offer-id="$resolve.offerId" logged-in-user="$resolve.loggedInUser"></pkp-storage-offer-details>',
            resolve: {
                loggedInUser: loginResult,
                offerId: ($route: angular.route.IRouteService) =>
                    $route.current.params.offerId
            }
        })
        .when('/storage/locations', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/storage/locations`);
            }
        })
        .when('/storage/locations/details/:id', {
            template: `<pkp-storage-location-details-page storage-location-id="$resolve.id"></pkp-storage-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/storage/locations/edit/:id', {
            template: `<pkp-storage-location-details-page storage-location-id="$resolve.id"></pkp-storage-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/storage/locations/create', {
            template: `<pkp-storage-location-details-page></pkp-storage-location-details-page>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/storage/booking', {
            template: `<pkp-wizard-host wizard-name="Storage FCFS booking" 
                                            template-url="app/storage-booking-wizard/storage-booking-wizard.component.html">
                           </pkp-wizard-host>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/access-denied', {
            template: require('app/security/access-denied.html')
        })
        .when('/permission-denied', {
            template: require('app/security/permission-denied.html')
        })
        .when('/maintenance-messages', {
            template: '<pkp-maintenance-messages></pkp-maintenance-messages>',
            resolve: {
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.TSO_USER)
            }
        })
        .when('/tso-user', {
            template: '',
            controller() {
                targetLocation.replace(`${appBaseUrl}/settings/profile`);
            }
        })
        .when('/lng/offers', {
            template:
                '<pkp-lng-offers favorite-filter-text="$resolve.role.favoriteFilterText" params="$resolve.params" logged-in-user="$resolve.authenticatedUser"></pkp-lng-offers>',
            resolve: {
                params: ($route: ng.route.IRouteService) =>
                    $route.current.params,
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) => {
                    let resolveAsSso = {
                        favoriteFilterText: 'Your SSO&rsquo;s only'
                    };
                    let resolveAsNonSso = {
                        favoriteFilterText: 'Your SSOs&rsquo; only'
                    };

                    return privilegeService.hasRole(USER_ROLES.SSO_ADMIN).then(
                        () => resolveAsSso,
                        () => resolveAsNonSso
                    );
                }
            }
        })
        .when('/lng/offers/create', {
            template:
                '<pkp-create-lng-offer authenticated-user="$resolve.authenticatedUser"></pkp-create-lng-offer>',
            resolve: {
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/lng/settings/company-information', {
            template: '',
            controller() {
                targetLocation.replace(
                    `/settings/organisation/company-information`
                );
            }
        })
        .when('/lng-settings/:activeSetting?', {
            template:
                '<pkp-lng-settings active-setting="$resolve.activeSetting" authenticated-user="$resolve.authenticatedUser">' +
                '</pkp-lng-settings>',
            resolve: {
                activeSetting: ($route: angular.route.IRouteService) =>
                    $route.current.params.activeSetting,
                authenticatedUser: loginResult,
                role: (privilegeService: PrivilegeService) =>
                    privilegeService.hasRole(USER_ROLES.SSO_ADMIN)
            }
        })
        .when('/lng/offers/details/:offerId/:action?', {
            template:
                '<pkp-lng-offer-details offer-id="$resolve.offerId" logged-in-user="$resolve.loggedInUser"></pkp-lng-offer-details>',
            resolve: {
                loggedInUser: loginResult,
                offerId: ($route: angular.route.IRouteService) =>
                    $route.current.params.offerId
            }
        })
        .when('/lng/locations', {
            template: `<pkp-lng-location-overview></pkp-lng-location-overview>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/lng/locations/details/:id', {
            template: `<pkp-lng-location-details-page storage-location-id="$resolve.id"></pkp-lng-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/lng/locations/edit/:id', {
            template: `<pkp-lng-location-details-page storage-location-id="$resolve.id"></pkp-lng-location-details-page>`,
            resolve: {
                id: ($route: ng.route.IRouteService) =>
                    $route.current.params.id,
                authenticatedUser: loginResult
            }
        })
        .when('/lng/locations/create', {
            template: `<pkp-lng-location-details-page></pkp-lng-location-details-page>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .when('/lng/booking', {
            template: `<pkp-wizard-host wizard-name="LNG FCFS booking" 
                                            template-url="app/lng-booking-wizard/lng-booking-wizard.component.html">
                           </pkp-wizard-host>`,
            resolve: {
                authenticatedUser: loginResult
            }
        })
        .otherwise({ redirectTo: '/start' });
}

angular.module('app').config(routes);
